import React from 'react';
import {
  Avatar,
  createTheme,
  Tabs,
  Tab,
  Box,
  Button,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  Card
} from '@mui/material';
import Client from '../Client';
import Config from '../ClientConfig';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import RapDecisions from './InventoryRapDecisions';
import { locationsList } from './simpleBiReportsApi';

let theme = createTheme();

const objectToCsv = function (data) {
  const csvRows = [];

  if (!data || !data.length) {
    return "No data";
  }

  const headers = Object.keys(data[0]);
  csvRows.push(headers.join(','));

  for (const row of data) {
    const values = headers.map(header => {
      const val = row[header];
      return `"${val}"`;
    });

    csvRows.push(values.join(','));
  }

  return csvRows.join('\n');
};

const stripDiacritics = (text = "") => {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ș/g, "s")
    .replace(/Ș/g, "S")
    .replace(/ț/g, "t")
    .replace(/Ț/g, "T");
};

class InventoryUI extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      client: new Client("none"),
      tabIndex: 0,
      isSubmitting: false,

      inventoryChecks: [],
      productEntries: [],

      inventoryData: {
        date: '',
        location: '',
        description: '',
        inventoryCheckLines_invCheck: []
      },

      tabs: ['List', 'New', 'Adjustments'],

      selectedInventory: null,
      showDetailDialog: false,

      location: 0,
      selectedLocationFilter: 0,
      locations: [{ iref: 0, name: "Select Location" }]
    };

    this.getData = this.getData.bind(this);
    this.selectLocation = this.selectLocation.bind(this);
    this.fetchInventoryChecks = this.fetchInventoryChecks.bind(this);
  }

  componentDidMount() {
    this.fetchInventoryChecks();
    this.getData();
  }

  fetchInventoryChecks = (locationId = null) => {
    const queryData = { limit: 100 };

    if (locationId && Number(locationId) !== 0) {
      queryData.location = locationId;
    }

    let data = this.state.client.getData(Config.API_URL_INVENTORY_CHECK, queryData);

    if (data) {
      let checks = [];

      if (Array.isArray(data)) {
        checks = data;
      } else if (Array.isArray(data.inventoryCheck)) {
        checks = data.inventoryCheck;
      } else if (data.inventoryCheck) {
        checks = [data.inventoryCheck];
      }

      this.setState({ inventoryChecks: checks });
    }
  };

  getData() {
    let queryData = {
      askQuantity: 1,
      isPurchased: 1,
      status: 2,
      orderBy_name: 1
    };

    let data = this.state.client.getData(Config.API_URL_PRODUCT, queryData);

    if (data) {
      const productEntries = Object.keys(data.products || {}).map((key) => {
        const fData = data.products[key];

        return {
          productId: fData.iref,
          productName: fData.name,
          unit: fData.unitType,
          quantity: '',
          reactKey: `${fData.iref}-${key}`
        };
      });

      this.setState({
        products: data,
        productEntries: productEntries
      });
    } else {
      alert('Failed to load products.');
    }

    let locations = locationsList(this.state.client);

    if (locations && Array.isArray(locations.locations)) {
      this.setState({ locations: locations.locations });
    }
  }

  handleLocationFilterChange = (e) => {
    const locationId = e.target.value;

    this.setState({ selectedLocationFilter: locationId }, () => {
      this.fetchInventoryChecks(locationId);
    });
  };

  selectLocation = (event) => {
    const value = event.target.value;

    this.setState(
      { location: value },
      function () {
        this.handleInventoryChange('location', value);
      }
    );
  }

  handleChange = (event, newValue) => {
    this.setState({ tabIndex: newValue });
  };

  handleInventoryChange = (field, value) => {
    this.setState(prev => ({
      inventoryData: {
        ...prev.inventoryData,
        [field]: value
      }
    }));
  }

  handleQuantityChange = (index, value) => {
    const updated = [...this.state.productEntries];
    updated[index].quantity = value || 0;

    this.setState({ productEntries: updated });
  }

  handleSubmit = () => {
    if (this.state.isSubmitting) return;

    const { inventoryData, productEntries } = this.state;

    if (!inventoryData.date || !inventoryData.location) {
      alert('Please fill in both Date and Location before submitting.');
      return;
    }

    const filteredProducts = productEntries
      .filter(p => p.quantity && !isNaN(p.quantity) && parseFloat(p.quantity) > 0)
      .map(p => ({
        product: p.productId,
        unit: p.unit,
        quantity: parseFloat(p.quantity),
        accountingQty: 0.0,
      }));

    if (filteredProducts.length === 0) {
      alert("Please enter at least one product line with quantity > 0.");
      return;
    }

    this.setState({ isSubmitting: true });

    const payload = {
      ...inventoryData,
      inventoryCheckLines_invCheck: filteredProducts
    };

    const result = this.state.client.pushData(Config.API_URL_INVENTORY_CHECK, payload);

    if (result && result.iref) {
      alert("Inventory check saved!");

      this.setState(prev => ({
        inventoryData: {
          date: '',
          location: '',
          description: '',
          inventoryCheckLines_invCheck: []
        },
        productEntries: prev.productEntries.map(p => ({ ...p, quantity: '' })),
        location: 0,
        tabIndex: 0,
        isSubmitting: false
      }));

      this.fetchInventoryChecks();
    } else {
      console.error('Save failed', result);

      const errorMessage = result?.message || "Save failed.";

      this.setState({ isSubmitting: false });
      alert(errorMessage);
    }
  };

  openInventoryDetail = (inv) => {
    this.setState({
      selectedInventory: inv,
      location: inv.location,
      date: inv.date,
      showDetailDialog: true
    });
  };

  closeInventoryDetail = () => {
    this.setState({
      showDetailDialog: false,
      selectedInventory: null
    });
  };

  onDownload = () => {
    const { selectedInventory, productEntries } = this.state;

    if (!selectedInventory || !Array.isArray(selectedInventory.inventoryCheckLines_invCheck)) {
      console.error("Missing data for CSV export.");
      return;
    }

    const linesWithNames = selectedInventory.inventoryCheckLines_invCheck.map(line => {
      const productInfo = productEntries.find(p => String(p.productId) === String(line.product));

      return {
        Product: productInfo?.productName || line.product || '-',
        Quantity: line.quantity ?? '-',
        Unit: productInfo?.unit === 0 ? 'pc' : productInfo?.unit === 1 ? 'kg' : '-',
      };
    });

    const locationName = selectedInventory.location?.name || this.state.location?.name || "location";
    const inventoryDate = selectedInventory.date || this.state.date || "";

    const link = document.createElement("a");
    link.download = "locationInventory_" + locationName + "_" + inventoryDate + ".csv";

    const csvData = objectToCsv(linesWithNames);
    let csvHeader = "Location Inventory\nDate: " + inventoryDate + "\nLocation: " + locationName;
    let csv = "\uFEFF" + csvHeader + "\n" + csvData;

    link.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    link.click();
  };

  handleExportProductsPDF = () => {
    const { productEntries } = this.state;
    const locationName = this.state.locations.find(l => String(l.iref) === String(this.state.inventoryData.location))?.name || "";
    const inventoryDate = this.state.inventoryData.date || "";

    if (!productEntries.length) {
      alert("No products available.");
      return;
    }

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "A4"
    });

    doc.setFontSize(16);
    doc.text("Inventory List", 40, 40);

    doc.setFontSize(12);
    doc.text("Location: " + stripDiacritics(locationName || ""), 40, 60);

    doc.setFontSize(11);
    doc.text("Date: " + (inventoryDate || ""), 40, 80);

    const rows = productEntries.map(p => [
      stripDiacritics(p.productName),
      p.unit === 0 ? "pc" : p.unit === 1 ? "kg" : "-",
      ""
    ]);

    autoTable(doc, {
      startY: 90,
      head: [["Product", "Unit", "Qty"]],
      body: rows,
      theme: "grid",
      styles: {
        cellPadding: 3,
        fontSize: 9,
        lineHeight: 1.0,
        overflow: "ellipsize",
        cellWidth: "wrap",
      },
      headStyles: {
        fillColor: [230, 230, 230],
        textColor: 20,
        fontStyle: "bold"
      },
      columnStyles: {
        0: { cellWidth: 200 },
        1: { cellWidth: 40 },
        2: { cellWidth: 120 }
      }
    });

    const pageCount = doc.getNumberOfPages();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.getWidth() - 60,
        doc.internal.pageSize.getHeight() - 20
      );
    }

    doc.save("inventory_product_list.pdf");
  };

  getLocations() {
    let locations = [{ iref: 0, name: "Select Location" }];

    if (this.state.locations) {
      Object.keys(this.state.locations).forEach(key => {
        let fData = this.state.locations[key];

        if (fData && fData.iref !== undefined) {
          let myData = { iref: fData.iref, name: fData.name };
          locations.push(myData);
        }
      });
    }

    return locations;
  }

  renderInventoryList = () => {
    let locations = this.getLocations();

    return (
      <Box
        sx={{
          height: "100%",
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            px: {
              xs: 1,
              sm: 1.5
            },
            py: {
              xs: 0.75,
              sm: 1
            },
            borderBottom: "1px solid rgba(0,0,0,0.08)"
          }}
        >
          <TextField
            id="standard-select-location-filter"
            select
            value={this.state.selectedLocationFilter}
            variant="standard"
            size="small"
            sx={{
              width: {
                xs: "100%",
                sm: 220
              },
              "& .MuiInputBase-input": {
                fontSize: {
                  xs: "16px",
                  sm: "14px"
                }
              },
              "& .MuiInputLabel-root": {
                fontSize: {
                  xs: "13px",
                  sm: "13px"
                }
              }
            }}
            onChange={this.handleLocationFilterChange}
          >
            {locations.map((option) => (
              <MenuItem key={option.iref} value={option.iref}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0
          }}
        >
          <TableContainer
            sx={{
              height: "100%",
              overflow: "auto"
            }}
          >
            <Table stickyHeader size="small" aria-label="inventory checks table">
              <TableHead>
                <TableRow>
                  <TableCell sx={this.headerCellSx("center")}>
                    <b>IREF</b>
                  </TableCell>

                  <TableCell sx={this.headerCellSx("left", 105)}>
                    <b>Date</b>
                  </TableCell>

                  <TableCell sx={this.headerCellSx("left")}>
                    <b>Location</b>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {(this.state.inventoryChecks || []).map((inv, idx) => (
                  <TableRow
                    key={idx}
                    hover
                    onClick={() => this.openInventoryDetail(inv)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={this.bodyCellSx("center", false)}>
                      {inv.iref || inv.id}
                    </TableCell>

                    <TableCell sx={this.bodyCellSx("left", false, 105)}>
                      {inv.date}
                    </TableCell>

                    <TableCell sx={this.bodyCellSx("left")}>
                      {inv.location?.name || inv.location?.uid || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    );
  };

  renderNewInventoryForm = () => {
    const { inventoryData, productEntries } = this.state;
    let locations = this.getLocations();

    const unitTypeMap = { 0: 'pc', 1: 'kg' };

    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');

    const minDateObj = new Date(today);
    minDateObj.setDate(minDateObj.getDate() - 30);

    const yyyyMin = minDateObj.getFullYear();
    const mmMin = String(minDateObj.getMonth() + 1).padStart(2, '0');
    const ddMin = String(minDateObj.getDate()).padStart(2, '0');
    const minDate = `${yyyyMin}-${mmMin}-${ddMin}`;

    const maxDate = `${yyyy}-${mm}-${dd}`;

    return (
      <Box
        sx={{
          height: "100%",
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            px: {
              xs: 1,
              sm: 1.5
            },
            py: {
              xs: 0.75,
              sm: 1
            },
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr 1fr",
              sm: "160px 220px auto 48px"
            },
            gap: {
              xs: 1,
              sm: 1.25
            },
            alignItems: "end",
            borderBottom: "1px solid rgba(0,0,0,0.08)"
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date"
              value={inventoryData.date ? dayjs(inventoryData.date) : null}
              onChange={(newValue) => {
                const formattedDate = newValue ? newValue.format('YYYY-MM-DD') : '';
                this.handleInventoryChange('date', formattedDate);
              }}
              minDate={dayjs(minDate)}
              maxDate={dayjs(maxDate)}
              inputFormat="DD.MM.YYYY"
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  size="small"
                  sx={this.filterSx()}
                  InputLabelProps={{ shrink: true }}
                />
              )}
              required
            />
          </LocalizationProvider>

          <TextField
            id="standard-select-location"
            select
            required
            value={inventoryData.location || 0}
            variant="standard"
            size="small"
            sx={this.filterSx()}
            onChange={this.selectLocation}
          >
            {locations.map((option) => (
              <MenuItem key={option.iref} value={option.iref}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ display: { xs: "none", sm: "block" } }} />

          <Button
            variant="outlined"
            sx={{
              minWidth: 40,
              px: 1,
              display: {
                xs: 'none',
                sm: 'inline-flex'
              },
              justifySelf: "end"
            }}
            onClick={this.handleExportProductsPDF}
          >
            <FileDownloadIcon fontSize="small" />
          </Button>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0
          }}
        >
          <TableContainer
            sx={{
              height: "100%",
              overflow: "auto"
            }}
          >
            <Table stickyHeader size="small" aria-label="new inventory table">
              <TableHead>
                <TableRow>
                  <TableCell sx={this.headerCellSx("left")}>
                    <b>Product</b>
                  </TableCell>

                  <TableCell sx={this.headerCellSx("center", 48)}>
                    <b>Unit</b>
                  </TableCell>

                  <TableCell sx={this.headerCellSx("right", 116)}>
                    <b>Quantity</b>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {productEntries.map((entry, index) => (
                  <TableRow key={entry.reactKey || entry.productId}>
                    <TableCell sx={this.bodyCellSx("left", false)}>
                      {entry.productName}
                    </TableCell>

                    <TableCell sx={this.bodyCellSx("center", false, 48)}>
                      {unitTypeMap[entry.unit]}
                    </TableCell>

                    <TableCell sx={this.bodyCellSx("right", false, 116)}>
                      <TextField
                        type="text"
                        value={String(entry.quantity ?? '')}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const normalized = raw.replace(',', '.');

                          if (Number(entry.unit) === 1) {
                            if (/^-?\d*(?:\.\d{0,3})?$/.test(normalized)) {
                              this.handleQuantityChange(index, normalized);
                            }
                          } else {
                            if (/^-?\d*$/.test(normalized)) {
                              this.handleQuantityChange(index, normalized);
                            }
                          }
                        }}
                        onBlur={(e) => {
                          let v = String(e.target.value ?? '').replace(',', '.').trim();

                          if (v === '' || v === '-') {
                            this.handleQuantityChange(index, '');
                            return;
                          }

                          if (!/^[-]?\d+(\.\d+)?$/.test(v)) return;

                          if (Number(entry.unit) === 1) {
                            const [i, d = ''] = v.split('.');
                            const out = d.length ? `${i}.${d.slice(0, 3)}` : i;
                            this.handleQuantityChange(index, out);
                          } else {
                            this.handleQuantityChange(index, String(parseInt(v, 10)));
                          }
                        }}
                        inputProps={{
                          inputMode: Number(entry.unit) === 1 ? 'decimal' : 'numeric',
                          style: { textAlign: 'right' },
                        }}
                        InputProps={{
                          sx: {
                            py: 0.2,
                            height: '34px',
                            width: {
                              xs: '78px',
                              sm: '110px'
                            },
                            fontSize: {
                              xs: '16px',
                              sm: '14px'
                            },
                            backgroundColor: entry.unit === 1 ? '#e6f7ff' : 'inherit',
                          },
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box
          sx={{
            px: {
              xs: 1,
              sm: 1.5
            },
            py: {
              xs: 1,
              sm: 1.25
            },
            borderTop: "1px solid rgba(0,0,0,0.08)",
            display: "flex",
            justifyContent: "center"
          }}
        >
          <Button
            variant="contained"
            color="primary"
            sx={{
              minWidth: {
                xs: "100%",
                sm: 220
              },
              px: 3
            }}
            onClick={this.handleSubmit}
            disabled={this.state.isSubmitting}
          >
            Save Inventory Check
          </Button>
        </Box>
      </Box>
    );
  };

  renderInventoryDetailDialog = () => {
    const { selectedInventory } = this.state;

    if (!selectedInventory) return null;

    const unitTypeMap = { 0: 'pc', 1: 'kg' };

    return (
      <Dialog
        open={this.state.showDetailDialog}
        onClose={this.closeInventoryDetail}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: {
              xs: 1.5,
              sm: 2
            },
            pr: 7,
            fontSize: {
              xs: "15px",
              sm: "18px"
            },
            fontWeight: 700
          }}
        >
          Inventory Detail - {selectedInventory.iref || selectedInventory.id}

          <Button
            color="inherit"
            size="small"
            onClick={this.onDownload}
            variant="contained"
            sx={{
              maxWidth: '36px',
              minWidth: '36px',
              ml: 1,
              display: {
                xs: 'none',
                sm: 'inline-flex'
              }
            }}
          >
            <Avatar sx={{ width: 24, height: 24, bgcolor: theme.palette.success.light }}>
              <FileDownloadIcon fontSize="small" />
            </Avatar>
          </Button>

          <IconButton
            aria-label="close"
            onClick={this.closeInventoryDetail}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            px: {
              xs: 1.5,
              sm: 2
            }
          }}
        >
          <Typography
            sx={{
              fontSize: {
                xs: "13.5px",
                sm: "14px"
              }
            }}
          >
            Date: {selectedInventory.date}
          </Typography>

          <Typography
            sx={{
              fontSize: {
                xs: "13.5px",
                sm: "14px"
              }
            }}
          >
            Location: {selectedInventory.location?.name || '-'}
          </Typography>

          {selectedInventory.inventoryCheckLines_invCheck?.length > 0 && (
            <Box mt={2}>
              <TableContainer
                sx={{
                  maxHeight: {
                    xs: "65vh",
                    sm: "70vh"
                  },
                  overflow: "auto"
                }}
              >
                <Table stickyHeader size="small" aria-label="inventory detail table">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={this.headerCellSx("left")}>
                        <b>Product</b>
                      </TableCell>

                      <TableCell sx={this.headerCellSx("center", 48)}>
                        <b>Unit</b>
                      </TableCell>

                      <TableCell sx={this.headerCellSx("right", 90)}>
                        <b>Quantity</b>
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {selectedInventory.inventoryCheckLines_invCheck.map((line, idx) => {
                      const product = this.state.productEntries.find(
                        p => String(p.productId) === String(line.product)
                      );

                      return (
                        <TableRow key={idx}>
                          <TableCell sx={this.bodyCellSx("left", false)}>
                            {product?.productName || line.product || '-'}
                          </TableCell>

                          <TableCell sx={this.bodyCellSx("center", false, 48)}>
                            {unitTypeMap[product?.unit] || '-'}
                          </TableCell>

                          <TableCell sx={this.bodyCellSx("right", false, 90)}>
                            {line.quantity?.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  render() {
    const { tabIndex, tabs } = this.state;

    return (
      <Card
        sx={{
          borderRadius: "14px",
          overflow: "hidden",
          maxWidth: "100%",
          height: {
            xs: "calc(100vh - 92px)",
            md: "calc(100vh - 104px)"
          },
          minHeight: {
            xs: 500,
            sm: 540
          },
          display: "flex",
          flexDirection: "column"
        }}
      >
        <Box
          sx={{
            background: "#f6f4ef",
            borderBottom: "1px solid rgba(0,0,0,0.08)"
          }}
        >
          <Tabs
            value={tabIndex}
            onChange={this.handleChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: {
                xs: 40,
                sm: 42
              },
              px: {
                xs: 0.25,
                sm: 0.5
              },
              "& .MuiTab-root": {
                minHeight: {
                  xs: 42,
                  sm: 44
                },
                minWidth: "auto",
                textTransform: "none",
                fontSize: {
                  xs: "13px",
                  sm: "14px"
                },
                fontWeight: 700,
                px: {
                  xs: 1.3,
                  sm: 1.8
                }
              }
            }}
          >
            {tabs.map((label, idx) => (
              <Tab key={idx} label={label} />
            ))}
          </Tabs>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0
          }}
        >
          {tabIndex === 0 && this.renderInventoryList()}
          {tabIndex === 1 && this.renderNewInventoryForm()}
          {tabIndex === 2 && (
            <Box
              sx={{
                height: "100%",
                overflow: "auto",
                p: {
                  xs: 1,
                  sm: 1.5
                }
              }}
            >
              <RapDecisions client={this.state.client} />
            </Box>
          )}
        </Box>

        {this.renderInventoryDetailDialog()}
      </Card>
    );
  }

  filterSx() {
    return {
      width: "100%",
      minWidth: 0,
      "& .MuiInputBase-input": {
        fontSize: {
          xs: "16px",
          sm: "14px"
        }
      },
      "& .MuiInputLabel-root": {
        fontSize: {
          xs: "13px",
          sm: "13px"
        }
      }
    };
  }

  headerCellSx(align, minWidth = null) {
    return {
      padding: {
        xs: "8px 7px",
        sm: "7px"
      },
      textAlign: align,
      whiteSpace: "nowrap",
      minWidth: minWidth,
      fontSize: {
        xs: "13px",
        sm: "13px",
        md: "14px"
      },
      backgroundColor: "#f6f4ef"
    };
  }

  bodyCellSx(align, strong = false, minWidth = null) {
    return {
      padding: {
        xs: "8px 7px",
        sm: "7px"
      },
      textAlign: align,
      whiteSpace: align === "left" ? "normal" : "nowrap",
      minWidth: minWidth,
      fontSize: {
        xs: "13.5px",
        sm: "13px",
        md: "14px"
      },
      fontWeight: strong ? 600 : 400,
      lineHeight: 1.35
    };
  }
}

export default InventoryUI;
